"use client";

import { useAuth } from "@/contexts/AuthContext";
import Image from "next/image";
import LoginForm from "@/components/LoginForm";
import {
  ActionButtonsPanel,
  CurrencyRates,
  RecentTransactions,
} from "@/components/home";
import { useState, useRef, useEffect } from "react";

export default function Home() {
  const {user, loading, signOut} = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await signOut();
      setIsDropdownOpen(false);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };


  function capitalizarPrimeraLetra(texto: string) {
    if (!texto) return '';
    return texto.charAt(0).toUpperCase() + texto.slice(1);
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  // If user is not logged in, show login page
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-2 w-full h-screen p-5">
          <div className="flex items-center justify-center">
            <LoginForm/>
          </div>
          <div className="hidden md:block relative w-full h-full overflow-hidden">
            <Image
              src="/layout-image.svg"
              alt="public-layout-image"
              fill
              className="object-cover rounded-2xl"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="bg-black p-8 relative overflow-hidden h-[259px] px-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10">
            <div className="md:col-span-1">
              <div className="space-y-2 mt-28">
                <h1 className="text-3xl font-bold text-white">
                  Hello,{" "}
                  {capitalizarPrimeraLetra(user.user_metadata?.name ||
                    user.email?.split("@")[0] ||
                    "User")}
                </h1>
                <p className="text-gray-300 text-lg">
                  Welcome to Zulu Transfer
                </p>
              </div>
            </div>

            {/* Right Section - User Actions */}
            <div className="md:col-span-2 flex justify-end items-start space-x-4">
              {/* User Avatar with Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center hover:bg-blue-600 transition-colors cursor-pointer"
                >
                  <span className="text-white font-semibold text-sm">
                    {user.user_metadata?.name
                      ? user.user_metadata.name
                        .split(" ")
                        .map((n: string) => n[0])
                        .join("")
                        .toUpperCase()
                        .slice(0, 2)
                      : user.email?.split("@")[0]?.slice(0, 2).toUpperCase() ||
                      "U"}
                  </span>
                </button>

                {isDropdownOpen && (
                  <div
                    className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">
                        {user.user_metadata?.name || "User"}
                      </p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                      </svg>
                      Sign Out
                    </button>
                  </div>
                )}
              </div>

              {/* App Name */}
              <div className="text-white font-semibold text-lg">Zulu</div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-24">
        {/* Action Buttons Panel */}
        <div className="mb-8">
          <ActionButtonsPanel/>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-[344px_1fr] gap-8 items-start">
          {/* Left Column - Currency Rates */}
          <div className="order-2 lg:order-1">
            <CurrencyRates/>
          </div>

          {/* Right Column - Recent Transactions (takes remaining space) */}
          <div className="order-1 lg:order-2 w-full">
            <RecentTransactions/>
          </div>
        </div>
      </div>
    </div>
  );
}
